import { CloseIcon } from 'assets/icons/CloseIcon'
import { PlusIcon } from 'assets/icons/PlusIcon'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { ISelectValue } from 'constants/globalTypes'
import { addInventoryIssue } from 'features/inventory/inventoryIssuesSlice'
import useModalClose from 'hooks/useModalClose'
import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { Button, CursorPointer, Flex, ModalScrollContainer, SquareIcon, ThinSeparator } from 'styling/GlobalStyles'
import { BodyText, TitleText } from 'styling/Typography'
import useFocusFirstInput from 'utils/useFocusFirstInput'

export const InventoryIssueModal = ({ closeModal }: { closeModal: any }) => {
    const [state, setState] = useState<IInventoryIssueModal>({
        transactionDate: null,
        fromLocation: null,
        assignedUser: null,
        lines: [
            { item: null, quantity: null, id: new Date().getMilliseconds(), lineNotes: '' }
        ]
    })
    const dispatch = useDispatch<AppDispatch>()
    useFocusFirstInput()
    useModalClose(closeModal)

    const handleSave = async () => {
        const resp = await dispatch(addInventoryIssue(state))
        if (resp.meta.requestStatus === 'fulfilled') {
            closeModal()
        }
    }


    const handleSelect = (value: ISelectValue, name: string) => {
        setState({ ...state, [name]: value })
    }

    const addLine = () => {
        setState(prev => ({ ...prev, lines: [...prev.lines, { id: new Date().getMilliseconds(), item: null, quantity: null, lineNotes: '' }] }))
    }

    const handleSelectItem = (value: ISelectValue, name: string, id: number) => {
        setState(prev => ({
            ...prev, lines: prev.lines.map(b => {
                if (b.id !== id) {
                    return b
                } else {
                    return { ...b, [name]: value }
                }
            })
        }))
    }

    const removeLine = (id: number) => {
        setState(prev => ({
            ...prev, lines: prev.lines.filter(b => b.id !== id)
        }))
    }

    const handleLine = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
        const { valueAsNumber, value, name } = e.target
        setState(prev => ({
            ...prev, lines: prev.lines.map(b => {
                if (b.id !== id) {
                    return b
                } else {
                    return { ...b, [name]: name == 'quantity' ? valueAsNumber : value }
                }
            })
        }))
    }

    const isDisabled = useMemo(() => {
        return state.lines.length === 0 || state.lines.some(l => !l.item || !l.quantity) || !state.fromLocation?.value || !state.assignedUser?.value || !state.transactionDate
    }, [state])

    return <Modal black>
        <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold' >Add Issue</TitleText>
            <CursorPointer onClick={closeModal} >
                <CloseIcon />
            </CursorPointer>
        </Flex>
        <ModalScrollContainer>
            <Flex column between center >
                <Flex column gap='1rem' width='100%' >
                    <HonestSelectAsyncInput
                        link='users/basic'
                        labelText='Assigned Employee'
                        name='assignedUser'
                        onChange={handleSelect}
                        parserFunction={(data: any) => data.data.map((i: any) => ({ label: i.fullName, value: i.id }))}
                        defaultValue={state.assignedUser?.label}
                        nomarg
                        required
                    />
                    <Flex gap='1rem' >
                        <CalendarInput
                            labelText='Transaction Date'
                            defaultValue={state.transactionDate}
                            name='transactionDate'
                            onChange={(v) => setState(prev => ({ ...prev, transactionDate: v }))}
                            width='100%'
                        />
                        <HonestSelectAsyncInput
                            nomarg
                            link='locations'
                            placeholder='Search locations'
                            name='fromLocation'
                            labelText='From Location'
                            defaultValue={state?.fromLocation?.label}
                            onChange={handleSelect}
                            parserFunction={(data: any) => data.data.map((i: any) => ({ label: i.name, value: i.id }))}
                            required
                            invalid={!state.fromLocation?.value}
                        />
                    </Flex>

                    <Flex gap='3rem' center  >
                        <BodyText weight='bold' >Items</BodyText>
                        <SquareIcon style={{ marginTop: 16 }} onClick={addLine}  >
                            {PlusIcon}
                        </SquareIcon>
                    </Flex>
                    <Flex column mb='16'>
                        {state.lines.map(l => <><Flex gap='1rem' key={l.id} >
                            <Flex column width='100%' gap='1rem' >
                                <Flex gap='1rem' >
                                    <HonestSelectAsyncInput
                                        name='item'
                                        labelText='Item'
                                        nomarg
                                        onChange={(v, n) => handleSelectItem(v, n, l.id)}
                                        link='inventory/item/stock' // TODO ITEM
                                        additionalParams={{ locationId: state.fromLocation?.value }}
                                        defaultValue={l.item?.label}
                                        parserFunction={(data: any) => data.data.map((i: any) => ({ label: i.name, value: i.id }))}
                                        required
                                        invalid={!l.item?.value}
                                    />
                                    <Input placeholder='55' labelText='Quantity' value={l.quantity} type='number' name='quantity' onChange={e => handleLine(e, l.id)} nomarg required invalid={!l.quantity} />
                                </Flex>
                                <Input type='textarea' height='100px' placeholder='Enter notes' labelText='Notes' value={l.lineNotes} name='lineNotes' onChange={e => handleLine(e, l.id)} nomarg />
                            </Flex>

                            {state.lines.length > 1 && <CursorPointer onClick={() => removeLine(l.id)} >
                                <CloseIcon />
                            </CursorPointer>}
                        </Flex>
                            <ThinSeparator />
                        </>
                        )}
                    </Flex>

                </Flex>
            </Flex>
        </ModalScrollContainer>
        <Flex width='100%' justcenter mt='16' >
            <Button disabled={isDisabled} orange onClick={handleSave} >Save</Button>
        </Flex>
    </Modal>
}

export interface IInventoryIssueModal {
    id?: number
    transactionDate: Date
    assignedUser: ISelectValue
    fromLocation: ISelectValue
    lines: { id?: number, item: ISelectValue, quantity: number, lineNotes: string }[]
}